import type { ProductType } from '@/types'
import { useCallback, useEffect, useState } from 'react'

export type UseSearchFormTabStateParams = {
  initialTab: ProductType
  onFormChange: (productType: ProductType) => void
}

export type UseSearchFormTabStateResult = {
  activeTab: ProductType
  changeTab: (productType: ProductType) => boolean
}

/**
 * Shared tab-state manager for SearchFormTabs consumers.
 *
 * Keeps local `activeTab` in sync when `initialTab` changes externally (e.g.
 * browser back/forward updating URL-driven `currentTab`).
 *
 * `changeTab` returns `true` when the tab actually changed (i.e. different
 * from the current value) and `false` when it was a re-click on the already-
 * active tab. Consumers use the return value to layer their own side effects
 * (analytics, opening drawers, etc.).
 */
export default function useSearchFormTabState({
  initialTab,
  onFormChange,
}: UseSearchFormTabStateParams): UseSearchFormTabStateResult {
  const [activeTab, setActiveTab] = useState(initialTab)

  useEffect(() => {
    setActiveTab(initialTab)
  }, [initialTab])

  const changeTab = useCallback(
    (productType: ProductType): boolean => {
      if (productType === activeTab) return false
      setActiveTab(productType)
      onFormChange(productType)
      return true
    },
    [activeTab, onFormChange],
  )

  return { activeTab, changeTab }
}
